import type { Distance, Rectangle } from "@pdfking/math"
import { InfinityQ, Max, ToNumber, Total } from "@pdfking/math"
import { CM } from "@pdfking/quantity"
import { NullQ } from "@pdfking/structs"
import type { GrowTemplate } from "@pdfking/templates"

import type { ImpositionFill, ImpositionSchema } from "#internal"
import type { Numbering } from "#internal"
import type { Information, InformationField, SheetMath } from "#public"

import { ApplyOrientation } from "../utils/ApplyOrientation"
import { ComputeMaterialCutWastePercent } from "./ComputeMaterialCutWaste"
import { FillSheets } from "./FillSheets"

const fmt_it = (n: number, decimals: number): string => {
  const rounded = ToNumber(n.toFixed(decimals))
  return String(rounded).replace(".", ",")
}

const sig_description = (sig: number[]): string => {
  const groups: [number, number][] = []
  for (const val of sig) {
    const last = groups[groups.length - 1]
    if (last && last[0] === val) last[1]++
    else groups.push([val, 1])
  }
  return groups.map(([val, count]) => `${count}x${val}`).join(" + ")
}

const size_cm = ({ width, height }: Rectangle<Distance>): string =>
  `${fmt_it(width / CM(1), 2)}x${fmt_it(height / CM(1), 2)} cm`

const GROW_SIZE_LABEL: Partial<Record<string, string>> = {
  book: "Libro",
  cover: "Copertina",
  box: "Scatola",
  fold: "Piega",
}

const primary_grow_kind = (components: ImpositionSchema["component_array"]): GrowTemplate => {
  const cover = components.find(c => c.grow_kind.is_cover)
  if (cover) return cover.grow_kind
  const book = components.find(c => c.grow_kind.is_book)
  if (book) return book.grow_kind
  const box = components.find(c => c.grow_kind.is_box)
  if (box) return box.grow_kind
  const fold = components.find(c => c.grow_kind.is_fold)
  if (fold) return fold.grow_kind
  return components[0]!.grow_kind
}

const size_label_for = (grow: GrowTemplate): string => {
  if (grow.is_book) return GROW_SIZE_LABEL.book!
  if (grow.is_cover) return GROW_SIZE_LABEL.cover!
  if (grow.is_box) return GROW_SIZE_LABEL.box!
  if (grow.is_fold) return GROW_SIZE_LABEL.fold!
  return "Formato"
}

export const BuildInformations = (
  input: ImpositionSchema,
  numbering: Numbering,
  has_book: boolean
): { fields: InformationField[]; informations: Information[] } => {
  const inner = ApplyOrientation(input.size, input.orientation_kind)
  const w_cm = fmt_it(inner.width / CM(1), 2)
  const h_cm = fmt_it(inner.height / CM(1), 2)
  const depth_raw = input.size_depth
  const depth_str = depth_raw === 0 ? "0 um" : `${fmt_it(depth_raw / CM(0.1), 2)} mm`
  const raw_sig = numbering.informations.signature_size_for_binding
  const grow = primary_grow_kind(input.component_array)

  // Per-component material cut-waste — formerly form-field warnings, now folded
  // into the informations list (the dispatch no longer carries `forms.warnings`).
  const waste: Information[] = input.component_array.flatMap(component => {
    const percent = ComputeMaterialCutWastePercent(component)
    return NullQ(percent)
      ? []
      : [
          {
            level: "warning",
            content: [`In fase di taglio verrà scartato il ${percent}% del materiale.`],
          },
        ]
  })

  if (grow.is_box && input.component_array.every(c => c.grow_kind.is_box)) {
    return { fields: [], informations: waste }
  }
  const size_label = has_book ? GROW_SIZE_LABEL.book! : size_label_for(grow)

  const fields: InformationField[] = [{ name: size_label, text: `${w_cm}x${h_cm} cm` }]

  if (has_book || grow.is_cover) {
    fields.push({ name: "Dorso", text: depth_str })
  } else if (grow.is_box) {
    const box_depth = input.component_array[0]?.sheet_depth ?? depth_raw
    fields.push({
      name: "Profondità",
      text: box_depth === 0 ? "0 um" : `${fmt_it(box_depth / CM(0.1), 2)} mm`,
    })
  }

  let sig = raw_sig.length > 0 ? raw_sig : []
  if (sig.length === 0 && has_book) {
    const book_component = input.component_array.find(c => c.grow_kind.is_book)
    const sides = book_component?.grow_kind.sides_tuple[0]
    const reps = Max(...input.component_array.map(c => c.repetitions || 1))
    if (sides && reps > 1) sig = [reps]
  }
  // Folded, the pages are its signatures' four each; loose (no fold), the
  // printed pages counted one by one.
  if (sig.length > 0) {
    fields.push({ name: "Pagine", text: String(Total(...sig) * 4) })
    fields.push({ name: "Segnature", text: sig_description(sig) })
  } else if (numbering.printed.length > 0) {
    fields.push({ name: "Pagine", text: String(numbering.printed.length) })
  }

  return { fields, informations: waste }
}

/** For staff, the sheets each component comes to, written out as the backend
 * works them out (prepress `_fill_sheets`): the order's products times the
 * component's copies, divided job by job by the copies a press sheet takes,
 * then by the press sheets a stock sheet takes, rounding up each time. */
export const BuildSheetMath = (input: ImpositionSchema, fills: ImpositionFill[]): SheetMath[] =>
  input.component_array.map((component, c) => {
    const fill = fills[c]!
    const copies = input.quantity * component.copies
    const { jobs, sheets } = FillSheets(copies, fill)
    const material = component.material
    return {
      name: component.label || "",
      text: `${
        component.copies === 1
          ? copies
          : `${input.quantity} prodotti × ${component.copies} = ${copies}`
      } copie`,
      steps: [
        ...component.job_array.map((job, j) => ({
          name: size_cm(job.machine_size),
          text: `⌈${j ? jobs[j - 1] : copies} ÷ ${fill.jobs[j]}⌉ = ${jobs[j]} fogli macchina`,
        })),
        ...(InfinityQ(material.width)
          ? []
          : [
              {
                name: `Materiale ${material.name}`,
                text: `⌈${jobs.at(-1) ?? copies} ÷ ${fill.fill_molt}⌉ = ${sheets} fogli`,
              },
            ]),
      ],
    }
  })
